import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface CreateGrievanceFormProps {
  onGrievanceCreated: () => void;
}

export const CreateGrievanceForm = ({ onGrievanceCreated }: CreateGrievanceFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentOrganization, user } = useAuth();
  const { logActivity } = useActivityLogger();
  
  const form = useForm({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
    },
  });

  const onSubmit = async (values: { title: string; description: string; priority: string }) => {
    if (!currentOrganization || !user) {
      toast.error('You must be logged in with an organization selected to create a grievance');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // First check if Legal department exists
      const { data: legalDept, error: deptError } = await supabase
        .from('departments')
        .select('id')
        .eq('name', 'Legal')
        .eq('organization_id', currentOrganization.organization.id)
        .maybeSingle();
      
      let departmentId;
      
      // If Legal department doesn't exist, create it
      if (!legalDept) {
        console.log('Legal department not found, creating it');
        const { data: newDept, error: createError } = await supabase
          .from('departments')
          .insert({
            name: 'Legal',
            description: 'Department for handling legal matters and grievances',
            organization_id: currentOrganization.organization.id
          })
          .select('id')
          .single();
        
        if (createError || !newDept) {
          console.error('Error creating legal department:', createError);
          toast.error('Failed to create Legal department');
          setIsSubmitting(false);
          return;
        }
        
        departmentId = newDept.id;
      } else {
        departmentId = legalDept.id;
      }
      
      // Create the grievance
      const { error } = await supabase
        .from('grievances')
        .insert([
          {
            title: values.title,
            description: values.description,
            priority: values.priority,
            status: 'open',
            organization_id: currentOrganization.organization.id,
            department_id: departmentId,
            created_by: user.id,
          },
        ]);
      
      if (error) {
        console.error('Error creating grievance:', error);
        toast.error('Failed to create grievance');
        return;
      }
      
      // Log activity
      await logActivity({
        action: 'create',
        resourceType: 'grievance',
        details: { title: values.title, priority: values.priority }
      });
      
      toast.success('Grievance created successfully');
      form.reset();
      onGrievanceCreated();
    } catch (error) {
      console.error('Error in creating grievance:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Brief title of your grievance" {...field} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Provide detailed information about your grievance" 
                  className="h-32" 
                  {...field} 
                  required 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority level" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Choose the appropriate priority level for your grievance
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Grievance'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
